import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { ME_KEY } from "@/auth/member";
import { setForbiddenListener } from "@/auth/session";
import { AuthGate } from "@/components/auth-gate";

import App from "./App";
import "./index.css";
import LogoutCallback from "./routes/LogoutCallback";

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // The bank changes when someone ingests or reviews, not continuously. Refetching
      // on every window focus makes a reviewer's table jump under them mid-read.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      // A 400 means the request was wrong and will be wrong again; only retry the
      // ones that might not be.
      retry: (failureCount, error) =>
        failureCount < 2 && !(error instanceof Error && /^\s*(4\d\d|question |job )/.test(error.message)),
    },
  },
});

// A 403 means the role may have changed under an open page; re-ask /me so the controls on
// screen, or the "no access" page, follow what the API now allows.
setForbiddenListener(() => void client.invalidateQueries({ queryKey: ME_KEY }));

const root = document.getElementById("root");
if (!root) throw new Error("index.html has no #root");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      {/* nginx serves index.html for any path that is not a file (ops/nginx/qbank.conf),
          which is what lets the router own the URL. */}
      <BrowserRouter>
        <Routes>
          {/* Outside the gate: Workspace lands here with no session left to check. */}
          <Route path="/logout-callback" element={<LogoutCallback />} />
          <Route
            path="*"
            element={
              <AuthGate>
                <App />
              </AuthGate>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
